<script lang="ts">
	import type { MailDataRequired } from '@sendgrid/mail'
	import Offcanvas from '$lib/public/layout/Offcanvas.svelte'
	import { appState } from '$lib/app-state.svelte'
	import { focusOnFirstError } from '$lib/focus'
	import FormInput from '$lib/public/forms/FormInput.svelte'
	import FormCheckbox from '$lib/public/forms/FormCheckbox.svelte'

	let message: Partial<MailDataRequired> = $state({
		subject: 'Question/comment from website',
		categories: ['question'],
		text: ''
	})

	let contact: Person = $state({
		id: 0,
		firstName: '',
		lastName: '',
		email: '',
		phone: '',
		optOut: false
	})

	let form: HTMLFormElement | undefined = $state()
	let focusField: HTMLInputElement | undefined = $state()
	let offCanvas: Offcanvas | undefined = $state()

	$effect(() => {
		if (appState.user) {
			contact.firstName = appState.user.firstName
			contact.lastName = appState.user.lastName
			contact.email = appState.user.email
			contact.phone = appState.user.phone
			contact.optOut = appState.user.optOut
		}
	})

	const onsubmit = async (event: SubmitEvent) => {
		event.preventDefault()

		if (form!.checkValidity()) {
			sendMessage()
			offCanvas?.close() // Sets appState.formInvalid = false

			appState.showToast({
				tone: 'success',
				title: 'Thank you',
				message: 'Your question/comment has been sent to our staff.'
			})
		} else {
			focusOnFirstError(form!)
			appState.formInvalid = true
		}
		message.text = '' // reset so last question doesn't reappear
	}

	const sendMessage = async () => {
		if (!contact) return

		const response = await fetch('/api/message', {
			method: 'POST',
			body: JSON.stringify({
				message: {
					...message,
					html: `<strong>From:</strong> ${contact.firstName} ${contact.lastName} (<a href="mailto:${contact.email}">${contact.email}</a>, <a href="tel:${contact.phone}">${contact.phone}</a>)<br/><br/>${message.text}`,
					replyTo: {
						name: `${contact.firstName} ${contact.lastName}`,
						email: contact.email
					}
				},
				contact
			}),
			headers: { 'Content-Type': 'application/json' }
		})

		if (!response.ok) {
			const msg = await response.json()
			console.error('Error sending message', msg)
		}
	}
</script>

<Offcanvas bind:this={offCanvas} id="contactOffcanvas" title="Contact Us" {focusField}>
	<form bind:this={form} {onsubmit} class="tw:grid tw:grid-cols-1 tw:gap-4" novalidate>
		<FormInput
			bind:value={contact.firstName}
			bind:input={focusField}
			id="messageFirstName"
			label="First Name"
			required={true}
			maxlength={20}
			autocomplete="given-name" />
		<FormInput
			bind:value={contact.lastName}
			id="messageLastName"
			label="Last Name"
			required={true}
			maxlength={20}
			autocomplete="family-name" />
		<FormInput
			bind:value={contact.email}
			id="messageEmail"
			type="email"
			label="Email"
			required={true}
			maxlength={80}
			autocomplete="email" />
		<FormInput
			bind:value={contact.phone}
			id="messagePhone"
			type="tel"
			label="Phone"
			maxlength={23}
			autocomplete="tel" />
		<FormInput
			bind:value={message.text}
			id="messageQuestion"
			rows={3}
			label="Your question or comment"
			required={true}
			maxlength={1024} />
		<FormCheckbox
			class="tw:-mt-8"
			bind:checked={contact.optOut}
			id="messageOptOut"
			label="Do not subscribe to newsletter" />

		<button
			type="submit"
			class="tw:mb-5 tw:w-full tw:bg-plum tw:text-white tw:border-0 tw:rounded-md tw:p-2 tw:text-xl tw:hover:bg-plum/90"
			>Submit</button>

		<div class="tw:text-center">
			Or call <a href="tel:4124014444" title="Call our main number">+1-412-401-4444</a>
		</div>
	</form>
</Offcanvas>
